// A20 — Persona Runtime Contracts. Editor surface for the contract
// (model lanes, skills, tools, memory, trigger/reaction policy,
// evidence contract, rubric, character budget, safety constraints).
// JSON-shaped fields are edited as monaco-style textareas; the
// character_budget is categorical.

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  deletePersonaContract,
  listPersonaContracts,
  upsertPersonaContract,
  type PersonaContractRow,
  type UpsertPersonaContractRequest,
} from "../api/client";

interface Props {
  workspaceId: number;
  agents?: Array<{
    agent_id: number;
    display_name: string;
    seat_key: string;
    kind?: string;
    model_backing?: string;
  }>;
}

const BLANK: UpsertPersonaContractRequest = {
  remit: "",
  task_tags: [],
  model_lanes: {},
  skills: [],
  tools: [],
  memory_privileges: {},
  trigger_contract: {},
  reaction_contract: {},
  evidence_contract: {},
  rubric: {},
  character_budget: "working",
  safety_constraints: {},
};

export function PersonaContractsPanel({ workspaceId, agents = [] }: Props) {
  const [contracts, setContracts] = useState<PersonaContractRow[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [draft, setDraft] = useState<UpsertPersonaContractRequest>(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPersonaContracts(workspaceId)
      .then((c) => {
        if (!cancelled) setContracts(c);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      );
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // The agent list comes from the workspace's existing personas — read
  // off `tables.agent` via SpacetimeDB. v1 minimal: we ask the user to
  // type an agent_id directly, since this surface is for power users.
  useEffect(() => {
    if (picked != null) {
      const existing = contracts.find((c) => c.agent_id === picked);
      if (existing) {
        setDraft({
          remit: existing.remit,
          task_tags: safeJson<string[]>(existing.task_tags_json, []),
          model_lanes: safeJson(existing.model_lanes_json, {}),
          skills: safeJson<unknown[]>(existing.skills_json, []),
          tools: safeJson<unknown[]>(existing.tools_json, []),
          memory_privileges: safeJson(existing.memory_privileges_json, {}),
          trigger_contract: safeJson(existing.trigger_contract_json, {}),
          reaction_contract: safeJson(existing.reaction_contract_json, {}),
          evidence_contract: safeJson(existing.evidence_contract_json, {}),
          rubric: safeJson(existing.rubric_json, {}),
          character_budget:
            (existing.character_budget as "minimal" | "working" | "expressive") ||
            "working",
          safety_constraints: safeJson(existing.safety_constraints_json, {}),
        });
      } else {
        setDraft(BLANK);
      }
    }
  }, [picked, contracts]);

  const sortedContracts = useMemo(
    () => [...contracts].sort((a, b) => a.agent_id - b.agent_id),
    [contracts],
  );
  const sortedAgents = useMemo(
    () => [...agents].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [agents],
  );
  const pickedAgent = useMemo(
    () => sortedAgents.find((agent) => agent.agent_id === picked) ?? null,
    [sortedAgents, picked],
  );

  const save = async () => {
    if (picked == null) {
      setError("pick an agent_id first");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await upsertPersonaContract(workspaceId, picked, draft);
      setInfo(`Saved contract for agent ${picked}.`);
      const c = await listPersonaContracts(workspaceId);
      setContracts(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (picked == null) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await deletePersonaContract(workspaceId, picked);
      setInfo(`Deleted contract for agent ${picked}.`);
      const c = await listPersonaContracts(workspaceId);
      setContracts(c);
      setDraft(BLANK);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-3"
      data-testid="persona-contracts-panel"
    >
      <header>
        <h3 className="text-xs font-semibold text-primary">
          Persona runtime contracts
        </h3>
        <p className="mt-1 text-[0.625rem] text-muted">
          The contract is what Vela's router, the orchestrator, Pearl, and
          Shore all read. Capability first (model lanes, skills, tools,
          memory); character is a budget on top.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[0.625rem] text-muted">agent_id:</label>
        <input
          type="number"
          value={picked ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            setPicked(v.length === 0 ? null : Number(v));
          }}
          placeholder="42"
          className="w-24 rounded-md border border-edge bg-surface px-2 py-1 font-code text-xs text-primary"
        />
        {sortedContracts.length > 0 && (
          <select
            value={picked ?? ""}
            onChange={(e) =>
              setPicked(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
          >
            <option value="">— pick from existing —</option>
            {sortedContracts.map((c) => (
              <option key={c.agent_id} value={c.agent_id}>
                agent {c.agent_id} (v{c.version})
              </option>
            ))}
          </select>
        )}
        {sortedAgents.length > 0 && (
          <select
            value={picked ?? ""}
            onChange={(e) =>
              setPicked(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
            data-testid="contract-agent-picker"
          >
            <option value="">— pick a persona —</option>
            {sortedAgents.map((agent) => (
              <option key={agent.agent_id} value={agent.agent_id}>
                {agent.display_name} · @{agent.seat_key}
              </option>
            ))}
          </select>
        )}
      </div>
      {pickedAgent && (
        <div className="rounded-md border border-edge bg-surface px-3 py-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-xs font-medium text-primary">
              {pickedAgent.display_name}
            </span>
            <span className="font-code text-[0.625rem] text-faint">
              @{pickedAgent.seat_key}
            </span>
            {pickedAgent.kind && (
              <span className="rounded bg-surface-raised px-1.5 py-0.5 font-code text-[0.625rem] text-muted">
                {pickedAgent.kind}
              </span>
            )}
            {pickedAgent.model_backing && (
              <span className="rounded bg-surface-raised px-1.5 py-0.5 font-code text-[0.625rem] text-muted">
                lane · {pickedAgent.model_backing}
              </span>
            )}
          </div>
          <p className="mt-1 text-[0.625rem] text-muted">
            Define what this persona is for, which lanes it should prefer,
            what evidence it owes, and how much character it gets to spend.
          </p>
        </div>
      )}
      <Field label="Remit (one sentence)" testid="contract-remit">
        <input
          type="text"
          value={draft.remit}
          onChange={(e) => setDraft({ ...draft, remit: e.target.value })}
          className="w-full rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
        />
      </Field>
      <Field label="Task tags (comma-separated)" testid="contract-task-tags">
        <input
          type="text"
          value={draft.task_tags.join(", ")}
          onChange={(e) =>
            setDraft({
              ...draft,
              task_tags: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          className="w-full rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
        />
      </Field>
      <Field label="Character budget" testid="contract-character-budget">
        <select
          value={draft.character_budget}
          onChange={(e) =>
            setDraft({
              ...draft,
              character_budget: e.target.value as
                | "minimal"
                | "working"
                | "expressive",
            })
          }
          className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
        >
          <option value="minimal">minimal — terse, precision lane</option>
          <option value="working">working — default</option>
          <option value="expressive">expressive — coordination/Vela</option>
        </select>
      </Field>
      <JsonField
        label="Model lanes (planning/implementation/visual/research/...)"
        value={draft.model_lanes}
        onChange={(v) =>
          setDraft({ ...draft, model_lanes: v as Record<string, unknown> })
        }
      />
      <JsonField
        label="Skills"
        value={draft.skills}
        onChange={(v) =>
          setDraft({ ...draft, skills: Array.isArray(v) ? v : [] })
        }
      />
      <JsonField
        label="Tools"
        value={draft.tools}
        onChange={(v) =>
          setDraft({ ...draft, tools: Array.isArray(v) ? v : [] })
        }
      />
      <JsonField
        label="Memory privileges"
        value={draft.memory_privileges}
        onChange={(v) =>
          setDraft({
            ...draft,
            memory_privileges: v as Record<string, unknown>,
          })
        }
      />
      <JsonField
        label="Trigger contract (watcher: source/threshold/cooldown/fire_limit)"
        value={draft.trigger_contract}
        onChange={(v) =>
          setDraft({ ...draft, trigger_contract: v as Record<string, unknown> })
        }
      />
      <JsonField
        label="Reaction contract (inject/post/arbitrate/raise_input/silent)"
        value={draft.reaction_contract}
        onChange={(v) =>
          setDraft({
            ...draft,
            reaction_contract: v as Record<string, unknown>,
          })
        }
      />
      <JsonField
        label="Evidence contract (what artefacts the persona must produce)"
        value={draft.evidence_contract}
        onChange={(v) =>
          setDraft({
            ...draft,
            evidence_contract: v as Record<string, unknown>,
          })
        }
      />
      <JsonField
        label="Rubric (Shore's evaluation criteria)"
        value={draft.rubric}
        onChange={(v) =>
          setDraft({ ...draft, rubric: v as Record<string, unknown> })
        }
      />
      <JsonField
        label="Safety constraints"
        value={draft.safety_constraints}
        onChange={(v) =>
          setDraft({
            ...draft,
            safety_constraints: v as Record<string, unknown>,
          })
        }
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || picked == null || !draft.remit.trim()}
          className="rounded-md border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          data-testid="contract-save"
        >
          {busy ? "Saving…" : "Save contract"}
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy || picked == null}
          className="rounded-md border border-muted px-3 py-1 text-xs font-medium text-muted hover:border-danger hover:text-danger disabled:opacity-50"
        >
          Delete
        </button>
        {info && <span className="text-[0.625rem] text-success">{info}</span>}
        {error && <span className="text-[0.625rem] text-danger">{error}</span>}
      </div>
    </section>
  );
}

function Field({
  label,
  testid,
  children,
}: {
  label: string;
  testid?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[0.625rem] text-muted">{label}</span>
      {children}
    </label>
  );
}

function JsonField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[0.625rem] text-muted">{label}</span>
      <textarea
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          try {
            const parsed = JSON.parse(v);
            setErr(null);
            onChange(parsed);
          } catch (e) {
            setErr(e instanceof Error ? e.message : "invalid JSON");
          }
        }}
        rows={4}
        className="w-full rounded-md border border-edge bg-surface px-2 py-1 font-code text-[0.625rem] text-primary"
      />
      {err && <span className="text-[0.625rem] text-danger">{err}</span>}
    </label>
  );
}

function safeJson<T>(s: string, fallback: T): T {
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}
